import {
  CompetitionLevel,
  Organization,
  postOrganizationsCreate,
  PostOrganizationsCreateData,
  postUsersInvitationsCreate,
  SportType,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  toast,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { z } from "zod";

import { useClipboard } from "@/hooks/use-clipboard";
import { addOrgPrefix } from "@/lib/utils";

import { AccountForm, accountFormSchema } from "./AccountForm";
import { Screen, ScreenManager } from "./ui/screen-manager";

export interface AccountCreationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refresh?: () => void;
}

export default function AccountCreationModal({
  refresh,
  open,
  onOpenChange,
}: AccountCreationModalProps) {
  const [generatedLink, setGeneratedLink] = useState("");
  const { hasCopied, onCopy } = useClipboard(generatedLink);

  const { mutateAsync: createOrganizationMutation, isPending } = useMutation({
    throwOnError: true,
    mutationFn: async (data: PostOrganizationsCreateData) => {
      const response = await postOrganizationsCreate(data);
      // TODO: Casting is necessary because the API type is not correct
      const organization = response.data as unknown as Organization;
      const invitationResponse = await postUsersInvitationsCreate({
        body: {
          roles: ["staff_admin"],
          duration: "forever",
          orgId: addOrgPrefix(organization.orgId),
        },
      });
      return invitationResponse.data?.invitationCode ?? "";
    },
    onError: () => {
      toast("Failed to generate link role", { variant: "error" });
    },
  });

  const handleSubmit = async (
    formData: z.infer<typeof accountFormSchema>,
    onSuccess: () => void,
  ) => {
    const invitationLink = await createOrganizationMutation({
      body: {
        name: formData.accountName,
        sport: formData.sport as SportType,
        competitionLevel: formData.competitionLevel as CompetitionLevel,
        city: formData.city,
        state: formData.state,
      },
    });
    const currentBaseUrl = window.location.origin;
    const url = `${currentBaseUrl}/claim-permissions/?token=${invitationLink}`;
    setGeneratedLink(url);
    onSuccess();
  };

  useEffect(() => {
    if (!open) setGeneratedLink("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onOpenChange(false)}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <ScreenManager defaultScreen="form">
          <Screen screenName="form">
            {({ setCurrentScreen }) => (
              <AccountForm
                setScrollContainer={setScrollContainer}
                showDivider={showDivider}
                onSubmit={(data) => {
                  handleSubmit(data, () => {
                    setCurrentScreen("link");
                    refresh?.();
                  });
                }}
                mode="creation"
                isPending={isPending}
                onOpenChange={onOpenChange}
              />
            )}
          </Screen>
          <Screen screenName="link">
            <>
              <DialogScrollableContent setScrollContainer={setScrollContainer}>
                <DialogHeader>
                  <DialogTitle>Organization Creation</DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="flex gap-2">
                    <Input value={generatedLink} readOnly />
                    <Button variant="secondary-flat-light" onClick={onCopy}>
                      {hasCopied ? "Copied!" : "Copy Link"}
                    </Button>
                  </div>
                </div>
              </DialogScrollableContent>

              <DialogFooter showDivider={showDivider}>
                <div className="flex w-full justify-start">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    className="underline"
                  >
                    Exit
                  </Button>
                </div>
              </DialogFooter>
            </>
          </Screen>
        </ScreenManager>
      )}
    </Dialog>
  );
}
